import React from 'react';
import { Link } from 'react-router-dom';
import {
  Search, TrendingUp, BarChart3, Briefcase, Eye, Newspaper,
  Bell, Brain, ShieldCheck, ArrowRight, Zap, LineChart, Target,
  Layers, SlidersHorizontal
} from 'lucide-react';

const HeroChart = () => (
  <svg viewBox="0 0 480 360" fill="none" xmlns="http://www.w3.org/2000/svg" className="hp-hero-chart" aria-hidden="true">
    {/* Dashboard card background */}
    <rect x="10" y="10" width="460" height="340" rx="16" fill="rgba(255,255,255,0.07)" stroke="rgba(255,255,255,0.12)" strokeWidth="1" />

    {/* Grid lines */}
    {[80, 130, 180, 230, 280].map(y => (
      <line key={y} x1="40" y1={y} x2="450" y2={y} stroke="rgba(255,255,255,0.06)" strokeWidth="1" />
    ))}
    {[100, 170, 240, 310, 380].map(x => (
      <line key={x} x1={x} y1="55" x2={x} y2="300" stroke="rgba(255,255,255,0.06)" strokeWidth="1" />
    ))}

    {/* Y-axis labels */}
    {[['$240', 80], ['$200', 130], ['$160', 180], ['$120', 230], ['$80', 280]].map(([label, y]) => (
      <text key={label} x="36" y={Number(y) + 4} textAnchor="end" fill="rgba(255,255,255,0.35)" fontSize="9" fontFamily="Arial, sans-serif">{label}</text>
    ))}

    {/* Candlestick bars */}
    {[
      { x: 80, o: 230, c: 210, h: 200, l: 240 },
      { x: 105, o: 210, c: 225, h: 205, l: 235 },
      { x: 130, o: 225, c: 195, h: 190, l: 230 },
      { x: 155, o: 195, c: 200, h: 185, l: 210 },
      { x: 180, o: 200, c: 175, h: 170, l: 210 },
      { x: 205, o: 175, c: 185, h: 165, l: 195 },
      { x: 230, o: 185, c: 160, h: 155, l: 195 },
      { x: 255, o: 160, c: 170, h: 150, l: 180 },
      { x: 280, o: 170, c: 145, h: 140, l: 180 },
      { x: 305, o: 145, c: 155, h: 135, l: 165 },
      { x: 330, o: 155, c: 130, h: 125, l: 165 },
      { x: 355, o: 130, c: 120, h: 110, l: 140 },
      { x: 380, o: 120, c: 105, h: 100, l: 130 },
      { x: 405, o: 105, c: 95, h: 88, l: 115 },
      { x: 430, o: 95, c: 85, h: 78, l: 105 },
    ].map(({ x, o, c, h, l }, i) => {
      const bullish = c < o;
      const color = bullish ? '#34d399' : '#f87171';
      const bodyTop = Math.min(o, c);
      const bodyH = Math.abs(o - c) || 2;
      return (
        <g key={i}>
          <line x1={x} y1={h} x2={x} y2={l} stroke={color} strokeWidth="1.2" opacity="0.7" />
          <rect x={x - 5} y={bodyTop} width="10" height={bodyH} rx="1" fill={color} opacity="0.85" />
        </g>
      );
    })}

    {/* Trend line overlay */}
    <polyline
      points="80,220 130,208 180,187 230,172 280,157 330,142 380,117 430,90"
      stroke="#38bdf8"
      strokeWidth="2.5"
      fill="none"
      strokeLinecap="round"
      strokeLinejoin="round"
      opacity="0.9"
    />

    {/* Gradient area under trend line */}
    <defs>
      <linearGradient id="heroAreaGrad" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor="#38bdf8" stopOpacity="0.25" />
        <stop offset="100%" stopColor="#38bdf8" stopOpacity="0" />
      </linearGradient>
    </defs>
    <polygon
      points="80,220 130,208 180,187 230,172 280,157 330,142 380,117 430,90 430,300 80,300"
      fill="url(#heroAreaGrad)"
    />

    {/* Buy signal marker */}
    <circle cx="180" cy="187" r="7" fill="#22c55e" opacity="0.9" />
    <text x="180" y="191" textAnchor="middle" fill="#fff" fontSize="8" fontWeight="bold" fontFamily="Arial, sans-serif">B</text>
    <rect x="157" y="165" width="46" height="16" rx="4" fill="rgba(28, 153, 74, 0.85)" />
    <text x="180" y="176" textAnchor="middle" fill="#fff" fontSize="8.5" fontWeight="600" fontFamily="Arial, sans-serif">BUY $160</text>

    {/* Sell signal marker */}
    <circle cx="330" cy="142" r="7" fill="#ef4444" opacity="0.9" />
    <text x="330" y="146" textAnchor="middle" fill="#fff" fontSize="8" fontWeight="bold" fontFamily="Arial, sans-serif">S</text>
    <rect x="306" y="120" width="48" height="16" rx="4" fill="rgba(175, 52, 52, 0.85)" />
    <text x="330" y="131" textAnchor="middle" fill="#fff" fontSize="8.5" fontWeight="600" fontFamily="Arial, sans-serif">SELL $200</text>

    {/* Current price indicator */}
    <line x1="430" y1="90" x2="455" y2="90" stroke="#38bdf8" strokeWidth="1.5" strokeDasharray="3 2" />
    <rect x="440" y="81" width="24" height="18" rx="4" fill="#38bdf8" />
    <text x="452" y="93" textAnchor="middle" fill="#fff" fontSize="8" fontWeight="700" fontFamily="Arial, sans-serif">$240</text>

    {/* Mini stat cards */}
    <rect x="40" y="30" width="88" height="38" rx="6" fill="rgba(255,255,255,0.1)" stroke="rgba(255,255,255,0.15)" strokeWidth="0.8" />
    <text x="50" y="45" fill="rgba(255,255,255,0.5)" fontSize="8" fontFamily="Arial, sans-serif">Portfolio</text>
    <text x="50" y="60" fill="#34d399" fontSize="12" fontWeight="700" fontFamily="Arial, sans-serif">+18.4%</text>

    <rect x="138" y="30" width="88" height="38" rx="6" fill="rgba(255,255,255,0.1)" stroke="rgba(255,255,255,0.15)" strokeWidth="0.8" />
    <text x="148" y="45" fill="rgba(255,255,255,0.5)" fontSize="8" fontFamily="Arial, sans-serif">Signals</text>
    <text x="148" y="60" fill="#38bdf8" fontSize="12" fontWeight="700" fontFamily="Arial, sans-serif">42 Buy</text>

    <rect x="236" y="30" width="88" height="38" rx="6" fill="rgba(255,255,255,0.1)" stroke="rgba(255,255,255,0.15)" strokeWidth="0.8" />
    <text x="246" y="45" fill="rgba(255,255,255,0.5)" fontSize="8" fontFamily="Arial, sans-serif">Win Rate</text>
    <text x="246" y="60" fill="#fff" fontSize="12" fontWeight="700" fontFamily="Arial, sans-serif">73.2%</text>

    {/* Volume bars at bottom */}
    {[
      80, 105, 130, 155, 180, 205, 230, 255, 280, 305, 330, 355, 380, 405, 430
    ].map((x, i) => {
      const h = [18, 12, 28, 14, 32, 20, 24, 16, 22, 10, 26, 15, 30, 19, 25][i];
      return (
        <rect key={i} x={x - 4} y={305 - h} width="8" height={h} rx="1.5" fill="rgba(56,189,248,0.25)" />
      );
    })}
    <text x="240" y="318" textAnchor="middle" fill="rgba(255,255,255,0.3)" fontSize="8" fontFamily="Arial, sans-serif">Volume</text>
  </svg>
);

const HomePage = ({ user }) => {
  return (
    <div className="home-page">
      {/* Hero */}
      <section className="hp-hero">
        <div className="hp-hero-split">
          <div className="hp-hero-text">
            <span className="hp-hero-badge">AI-Powered Stock Intelligence</span>
            <h1 className="hp-hero-title">
              Smarter Investing Starts&nbsp;Here
            </h1>
            <p className="hp-hero-subtitle">
              Harness AI-driven screeners, real-time signals, and portfolio analytics
              to discover opportunities and manage your investments with confidence.
            </p>
            <div className="hp-hero-cta">
              <Link to="/screener" className="hp-btn hp-btn-primary">
                <Search size={18} />
                Open Screener
              </Link>
              {!user && (
                <Link to="/login" className="hp-btn hp-btn-outline">
                  Get Started Free
                  <ArrowRight size={18} />
                </Link>
              )}
            </div>
          </div>
          <div className="hp-hero-graphic">
            <HeroChart />
            <span className="hp-illustrative-note">
              Chart shown for illustration only. Figures are examples, not actual
              or expected performance.
            </span>
          </div>
        </div>
      </section>

      {/* Features grid */}
      <section className="hp-section">
        <div className="hp-section-header">
          <h2>Everything You Need in One&nbsp;Platform</h2>
          <p>Professional-grade tools for every stage of your investment workflow.</p>
        </div>

        <div className="hp-features-grid">
          <Link to="/screener" className="hp-feature-card hp-feature-accent">
            <div className="hp-feature-icon">
              <SlidersHorizontal size={28} />
            </div>
            <h3>AI Stock Screener</h3>
            <p>
              Filter thousands of stocks by PE, RSI, sector, market cap &amp; more.
              Get momentum and mean-reversion Buy / Sell / Hold signals daily.
            </p>
            <span className="hp-feature-link">
              Open Screener <ArrowRight size={14} />
            </span>
          </Link>

          <Link to="/portfolio" className="hp-feature-card">
            <div className="hp-feature-icon">
              <Briefcase size={28} />
            </div>
            <h3>Portfolio Manager</h3>
            <p>
              Track multiple portfolios with buy prices, dates, and live
              performance metrics all in one place.
            </p>
            <span className="hp-feature-link">
              Manage Portfolios <ArrowRight size={14} />
            </span>
          </Link>

          <Link to="/watchlist" className="hp-feature-card">
            <div className="hp-feature-icon">
              <Eye size={28} />
            </div>
            <h3>Watchlists</h3>
            <p>
              Organise symbols into custom watchlists to stay on top of stocks
              you&rsquo;re researching or waiting to enter.
            </p>
            <span className="hp-feature-link">
              View Watchlists <ArrowRight size={14} />
            </span>
          </Link>

          <Link to="/movers" className="hp-feature-card">
            <div className="hp-feature-icon">
              <TrendingUp size={28} />
            </div>
            <h3>Market Movers</h3>
            <p>
              Spot the day&rsquo;s biggest gainers and losers at a glance&mdash;so
              you never miss a breakout or breakdown.
            </p>
            <span className="hp-feature-link">
              See Movers <ArrowRight size={14} />
            </span>
          </Link>

          <Link to="/news" className="hp-feature-card">
            <div className="hp-feature-icon">
              <Newspaper size={28} />
            </div>
            <h3>News &amp; Sentiment</h3>
            <p>
              AI-rated headlines scored 0&ndash;10, filterable by date and keyword,
              so you can gauge market sentiment instantly.
            </p>
            <span className="hp-feature-link">
              Read News <ArrowRight size={14} />
            </span>
          </Link>

          <Link to="/messages" className="hp-feature-card">
            <div className="hp-feature-icon">
              <Bell size={28} />
            </div>
            <h3>Alerts &amp; Notifications</h3>
            <p>
              Set price alerts and custom keywords. Receive real-time notifications
              so you can act when it matters.
            </p>
            <span className="hp-feature-link">
              View Alerts <ArrowRight size={14} />
            </span>
          </Link>
        </div>
      </section>

      {/* How it works */}
      <section className="hp-section hp-section-alt">
        <div className="hp-section-header">
          <h2>How It Works</h2>
          <p>Get up and running in three simple steps.</p>
        </div>

        <div className="hp-steps">
          <div className="hp-step">
            <div className="hp-step-number">1</div>
            <h3>Screen &amp; Discover</h3>
            <p>
              Use the AI screener to surface stocks matching your strategy&mdash;momentum,
              mean-reversion, or your own custom rules.
            </p>
          </div>

          <div className="hp-step-divider" />

          <div className="hp-step">
            <div className="hp-step-number">2</div>
            <h3>Research &amp; Analyse</h3>
            <p>
              Dive into interactive charts, financial statements, and AI-rated
              news sentiment for any symbol.
            </p>
          </div>

          <div className="hp-step-divider" />

          <div className="hp-step">
            <div className="hp-step-number">3</div>
            <h3>Track &amp; Act</h3>
            <p>
              Add positions to your portfolio, set alerts, and let the platform
              keep you informed as conditions change.
            </p>
          </div>
        </div>
      </section>

      {/* Capabilities highlight */}
      <section className="hp-section">
        <div className="hp-section-header">
          <h2>Built for Serious Investors</h2>
          <p>The analytical edge you&rsquo;ve been looking for.</p>
        </div>

        <div className="hp-capabilities">
          <div className="hp-capability">
            <Brain size={22} />
            <div>
              <h4>AI-Powered Signals</h4>
              <p>Data-driven models generate daily Buy, Sell, and Hold signals across the market for your own research.</p>
            </div>
          </div>

          <div className="hp-capability">
            <Target size={22} />
            <div>
              <h4>Custom Trading Rules</h4>
              <p>Define your own buy &amp; sell conditions and backtest them against historical data.</p>
            </div>
          </div>

          <div className="hp-capability">
            <LineChart size={22} />
            <div>
              <h4>Advanced Charting</h4>
              <p>TradingView-powered interactive charts with full technical analysis tools.</p>
            </div>
          </div>

          <div className="hp-capability">
            <BarChart3 size={22} />
            <div>
              <h4>Financial Statements</h4>
              <p>Income statements, balance sheets, and cash-flow data at your fingertips.</p>
            </div>
          </div>

          <div className="hp-capability">
            <Zap size={22} />
            <div>
              <h4>Real-Time Alerts</h4>
              <p>Price alerts and keyword-based notifications delivered the moment they trigger.</p>
            </div>
          </div>

          <div className="hp-capability">
            <Layers size={22} />
            <div>
              <h4>Multi-Strategy Support</h4>
              <p>Switch between Momentum and Mean Reversion strategies with a single click.</p>
            </div>
          </div>

          <div className="hp-capability">
            <ShieldCheck size={22} />
            <div>
              <h4>Secure &amp; Private</h4>
              <p>Your data is encrypted and never shared. Full control over your account and preferences.</p>
            </div>
          </div>

          <div className="hp-capability">
            <Search size={22} />
            <div>
              <h4>Smart Search</h4>
              <p>Instantly find any symbol or company with intelligent autocomplete suggestions.</p>
            </div>
          </div>
        </div>
      </section>

    </div>
  );
};

export default HomePage;
